/**
 * Products Store
 * Verwaltet Produkt-Daten
 */

import { writable, derived } from 'svelte/store';
import type { Product, ProductCategory } from '../types';
import { productsStorage } from '../services/storage';
import { frappeClient } from '../services/frappe';
import { syncService } from '../services/sync';

interface ProductsState {
  products: Product[];
  categories: ProductCategory[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  selectedCategory: string | null;
}

function createProductsStore() {
  // Initial State aus LocalStorage laden
  const initialProducts = productsStorage.load();

  const { subscribe, set, update } = writable<ProductsState>({
    products: initialProducts,
    categories: [],
    isLoading: false,
    error: null,
    searchQuery: '',
    selectedCategory: null
  });

  return {
    subscribe,

    /**
     * Lädt Produkte vom Server
     */
    async load(): Promise<void> {
      update((state) => ({ ...state, isLoading: true, error: null }));

      try {
        // Versuche Online-Sync
        if (syncService.isOnline()) {
          const products = await frappeClient.getProducts();
          productsStorage.save(products);
          update((state) => ({
            ...state,
            products,
            isLoading: false
          }));
        } else {
          // Offline: Verwende lokale Daten
          const products = productsStorage.load();
          update((state) => ({
            ...state,
            products,
            isLoading: false
          }));
        }
      } catch (error) {
        // Fallback auf lokale Daten
        const products = productsStorage.load();
        const errorMessage = error instanceof Error ? error.message : 'Fehler beim Laden';
        update((state) => ({
          ...state,
          products,
          isLoading: false,
          error: errorMessage
        }));
      }
    },

    /**
     * Fügt Produkt hinzu
     */
    add(product: Product): void {
      update((state) => {
        const products = [...state.products, product];
        productsStorage.save(products);
        return { ...state, products };
      });
    },

    /**
     * Aktualisiert Produkt
     */
    update(id: string, updates: Partial<Product>): void {
      update((state) => {
        const products = state.products.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        );
        productsStorage.save(products);
        return { ...state, products };
      });
    },

    /**
     * Entfernt Produkt
     */
    remove(id: string): void {
      update((state) => {
        const products = state.products.filter((p) => p.id !== id);
        productsStorage.save(products);
        return { ...state, products };
      });
    },

    /**
     * Setzt Such-Query
     */
    setSearchQuery(query: string): void {
      update((state) => ({ ...state, searchQuery: query }));
    },

    /**
     * Setzt ausgewählte Kategorie
     */
    setCategory(category: string | null): void {
      update((state) => ({ ...state, selectedCategory: category }));
    },

    /**
     * Gibt Produkt nach ID zurück
     */
    getById(id: string): Product | undefined {
      let product: Product | undefined;
      const unsubscribe = subscribe((state) => {
        product = state.products.find((p) => p.id === id);
      });
      unsubscribe();
      return product;
    },

    /**
     * Setzt Fehler
     */
    setError(error: string | null): void {
      update((state) => ({ ...state, error }));
    }
  };
}

export const productsStore = createProductsStore();

/**
 * Derived Store: Gefilterte Produkte
 */
export const filteredProducts = derived(productsStore, ($products) => {
  let filtered = $products.products;

  // Kategorie-Filter
  if ($products.selectedCategory) {
    filtered = filtered.filter(
      (p) => p.category === $products.selectedCategory
    );
  }

  // Such-Filter
  if ($products.searchQuery) {
    const query = $products.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.item_code?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
    );
  }

  return filtered;
});

/**
 * Derived Store: Kategorien aus Produkten extrahieren
 */
export const productCategories = derived(productsStore, ($products) => {
  const categoryMap = new Map<string, ProductCategory>();
  $products.products.forEach((product) => {
    if (product.category && !categoryMap.has(product.category)) {
      categoryMap.set(product.category, {
        id: product.category,
        name: product.category
      });
    }
  });
  return Array.from(categoryMap.values());
});

